import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'
import Modal from 'react-bootstrap-modal'
import DateInputWrapper from '../open-game-form/date-input-wrapper'
import SelectInputWrapper from '../open-game-form/select-input-wrapper'
import Fields from '../open-game-form/fields'
import FlashAlert from '../flash-alert'
import AdditionalGames from '../open-game-form/additional-games'
import Recurring from '../open-game-form/recurring'
import TextInput from './text-input'
import { ajaxGet, postForm } from '../../ajax'
import moment from 'moment'

function GameForm({payload, onChangePayload, onCreate, onLoading}) {
  const [baseFields, setBaseFields] = useState(null)
  const [alert, setAlert] = useState(null)
  const [showRecurring, setShowRecurring] = useState(false)
  const [subGames, setSubGames] = useState([])
  const [endDate, setEndDate] = useState(null)
  const [timeError, setTimeError] = useState(null)

  const { register, unregister, setError, clearErrors, getValues, setValue, handleSubmit, formState: { errors } } = useForm()

  useEffect(() => {
    if (payload) initForm(payload.form)
  }, [])

  const loadData = (params) => {
    onLoading(true)
    ajaxGet(`/admins/reservations/new.json?` + new URLSearchParams(params))
    .then(response => { if (response.ok) return response.json() })
    .then((json) => {
      onChangePayload(json)
      initForm(json.form)
      onLoading(false)
    })
  }

  const initForm = (data) => {
    for (const [key, value] of Object.entries(data)) { setValue(key, value) }
    setBaseFields(data.field_id)
    setTimeError(null)
  }
  
  const prepareFormData = (data) => {
    let formData = new FormData()
    formData.append('game[duration]', payload.duration)
    formData.append('game[times][]', payload.time)
    for (const key in data) {
      if (data[key] == undefined) continue
      if (key == 'times')
        data[key].forEach(v => v && formData.append('game[times][]', v))
      else
        formData.append(`game[${key}]`, data[key]) 
    }
    if (data.interval) formData.append('game[end_date]', endDate)
    formData.append('game[name]', 'Open Game')
    formData.append('game[open_game]', true)
    formData.append('game[field_ids][]', baseFields)
    subGames.forEach(game => formData.append('game[field_ids][]', game.field))
    if (data.cost != payload.cost) formData.set('game[cost]', payload.cost)
    return formData
  }
  
  const onSubmit = (data) => {
    if (!validateTime()) return
    if (!validateEndDate()) return

    const formData = prepareFormData(data)
    postForm(`/admins/reservations.json?`, formData)
    .then(response => { return response.json() })
    .then((json) => {
      if (json.alert)
        setAlert(json.alert)
      else
        onCreate()
    })
  }

  const validateTime = () => {
    if (payload.time)
      return true
    else {
      setTimeError({message: requiredMsg})
      return false
    }
  }

  const validateEndDate = () => {
    if (!showRecurring) return true

    var error = {}
    const interval = moment(new Date(endDate)).diff(new Date(baseDate), 'months', true)
    if (interval <= 0) error = { type: 'manual', message: 'Invalid interval'}
    if (interval > 12) error = { type: 'manual', message: 'Interval limit is 12 months'}
    if (!_.isEmpty(error)) {
      setError('end_date', error)
      return false
    }
    return true
  }

  const addSubGame = () => {
    setSubGames([...subGames, {field: baseFields, date: payload.date, hours: payload.hours}])
  }

  const removeSubGame = (index) => {
    const newSubGames = [...subGames.slice(0, index), ...subGames.slice(index + 1, subGames.length)]
    setSubGames(newSubGames)
  }

  const changeSubGame = (index, params) => {
    const newSubGames = [ 
      ...subGames.slice(0, index), 
      {...subGames[index], ...params },
      ...subGames.slice(index + 1, subGames.length) ]
    setSubGames(newSubGames)
  }

  const changeEndDate = (date) => {
    setEndDate(date)
    clearErrors('end_date')
  }

  const changeLocation = (locationId) => {
    const formParams = {
      'game[location_id]': locationId,
      'game[date]': payload.date,
    }
    loadData(formParams)
    setSubGames([])
  }

  const changeGameOptions = (params) => {
    const formParams = {
      'game[location_id]': getValues('location_id'),
      'game[duration]': payload.duration,
      'game[date]': payload.date,
      'game[times][]': payload.time,
      'game[field_ids][]': baseFields
    }
    loadData({...formParams, ...params})
  }

  const requiredMsg = 'Is required'
  
  if (!payload) return null
  const baseDate = payload.date

  return <div className='game-form'>
    { alert && <FlashAlert text={alert.text} type={alert.type} html={alert.html} constant centred onHide={() => setAlert(null)} />}
    <form className='form-horizontal open-game-form' onSubmit={handleSubmit(onSubmit)}>
      <div className='row'>
        <div className='col-xs-6'>
          <DateInputWrapper
            label='Date'
            onChange={(date) => changeGameOptions({'game[date]': (date || moment()).format('MM/DD/YYYY')})}
            date={moment(new Date(baseDate))}
            minDate={moment()}
          />
        </div>
        <div className='col-sm-2 hidden'>
          <SelectInputWrapper
            label='Duration'
            value={payload.duration}
            options={payload.durations}
            onChange={(e) => changeGameOptions({'game[duration]': e.target.value})}
          />
        </div>
        <div className='col-sm-2'>
          <SelectInputWrapper
            label='From'
            selectClassName='time-select'
            value={payload.time}
            options={payload.hours}
            error={timeError}
            onChange={(e) => changeGameOptions({'game[times][]': e.target.value})}
          />
        </div>
      </div>
      <div className='row'>
        { payload.locations && payload.locations.length > 1 && 
          <div className='col-sm-2'>
            <SelectInputWrapper 
              {...register('location_id')} 
              label='Location' 
              options={payload.locations}
              onChange={(e) => {
                changeLocation(e.target.value)
                setValue('location_id', e.target.value)
              }}
            />
          </div>
        }
      </div>

      { payload.fields &&
        <Fields
          selected={baseFields}
          fields={payload.fields}
          fieldIndex={0}
          onChangeField={(value) => changeGameOptions({'game[field_ids][]': value})}
        /> 
      }

      { baseFields  && 
        <AdditionalGames
          unregister={unregister}
          register={register}
          errors={errors}
          getValues={getValues}
          setValue={setValue}
          locationId={getValues('location_id')}
          fields={payload.fields}
          subGames={subGames}
          onAdd={addSubGame}
          onRemove={removeSubGame}
          onChange={changeSubGame}
          onLoading={onLoading}
        />
      }

      <div className='row'>
        <div className='col-sm-12'>
          <a className='btn btn-link clickable recurrings' onClick={() => setShowRecurring(!showRecurring)}>
            <i className='fa fa-lock'></i>
            &nbsp;Recurring Reservations
          </a>
        </div>
      </div>

      { showRecurring &&
        <Recurring 
          register={register}
          unregister={unregister}
          errors={errors}
          setValue={setValue}
          intervals={payload.intervals}
          baseDate={baseDate}
          endDate={endDate}
          onDateChange={changeEndDate}
        />
      }

      <Modal.Footer>
        { alert && <FlashAlert text={alert.text} type={alert.type} html={alert.html} constant onHide={() => setAlert(null)} />}
        <input type='submit' name='commit' value='Create Game' className='btn btn-primary' />
      </Modal.Footer>
    </form>
  </div>
}

GameForm.propTypes = {
  payload: PropTypes.object.isRequired,
  onChangePayload: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired
}

export default GameForm
